import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import {
    Container,
    Section,
    PageHero,
    RuleDivider,
    Chip,
} from "@/components/Primitives";

export default function Markets() {
    return (
        <>
            <Seo
                title="Markets"
                description="One engine, two arenas. The Aegis Line trades low-float gap-and-go stocks at the open and opening-range acceptance on the micro E-mini index futures (MES, MNQ)."
            />
            <PageHero
                eyebrow="Stocks & Futures — the two engines"
                title="One engine, two arenas."
                sub="The screening differs, the instruments differ — the discipline doesn't."
            />

            {/* Stocks */}
            <Section>
                <Container className="grid items-center gap-12 lg:grid-cols-2">
                    <Reveal>
                        <span className="eyebrow">Stocks — the morning movers</span>
                        <h2 className="mt-4 text-3xl leading-tight text-text-hi sm:text-4xl">
                            Low-float gap-and-go. First hour only.
                        </h2>
                        <p className="mt-6 text-base leading-relaxed text-text-lo">
                            The stock engine targets the highest-energy,
                            highest-structure window of the trading day: the
                            first hour, when micro- and small-cap names gap on
                            genuine volume and a real catalyst. The Aegis Line
                            screens for names that earned their place — real
                            relative volume against a 20-day baseline, a
                            confirmed reason for the gap, price in a tradeable
                            range. It reads the opening range, tracks VWAP
                            behavior, and trades the names that hold their
                            structure. Fast, disciplined, risk-bounded intraday
                            trading.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-2">
                            <Chip>Premarket screening</Chip>
                            <Chip>Catalyst-aware</Chip>
                            <Chip>Opening-range structure</Chip>
                            <Chip>First-hour focus</Chip>
                        </div>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <img
                            src="/images/markets_stocks.png"
                            alt="Abstract gap-and-go candlestick chart rising at the open with a gold ruled line"
                            className="w-full rounded-lg border border-haze"
                            loading="lazy"
                        />
                    </Reveal>
                </Container>
            </Section>

            <Container>
                <RuleDivider />
            </Container>

            {/* Futures */}
            <Section>
                <Container className="grid items-center gap-12 lg:grid-cols-2">
                    <Reveal className="lg:order-2">
                        <div className="flex items-center gap-3">
                            <span className="eyebrow">Futures — the index, at the bell</span>
                            <span className="rounded-sm border border-rule/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-rule">
                                On the roadmap
                            </span>
                        </div>
                        <h2 className="mt-4 text-3xl leading-tight text-text-hi sm:text-4xl">
                            Opening-range acceptance on the micro E-minis.
                        </h2>
                        <p className="mt-6 text-base leading-relaxed text-text-lo">
                            The futures engine trades the index micros — the
                            Micro E-mini S&amp;P (MES) and Micro E-mini Nasdaq
                            (MNQ) — during the regular session. The overnight
                            range and opening range are the structure to trade
                            against: where price accepts, where it rejects. Micro
                            contracts keep early risk small while the full
                            analysis and judgment stack runs exactly as it does
                            for stocks. The same standard, scaled to the
                            instrument.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-2">
                            <Chip tone="signal">MES + MNQ</Chip>
                            <Chip>Regular-hours session</Chip>
                            <Chip>Overnight + opening range</Chip>
                            <Chip>Micro-sized risk</Chip>
                        </div>
                    </Reveal>
                    <Reveal delay={0.12} className="lg:order-1">
                        <img
                            src="/images/markets_futures.png"
                            alt="Abstract opening range channel between two gold ruled lines with candles testing acceptance"
                            className="w-full rounded-lg border border-haze"
                            loading="lazy"
                        />
                    </Reveal>
                </Container>
            </Section>

            <Section className="border-t border-haze bg-ink-soft/40">
                <Container>
                    <Reveal>
                        <p className="mx-auto max-w-2xl text-center text-xl leading-relaxed text-text-hi sm:text-2xl">
                            One engine. Two arenas. The instrument changes —{" "}
                            <span className="text-rule">the aegis doesn't.</span>
                        </p>
                    </Reveal>
                </Container>
            </Section>
        </>
    );
}
